import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { FormattedMessage } from 'react-intl';

import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { useAppDispatch } from '@/store/hooks.ts';
import { getRfsId, selectOptionHedge, selectQuoteHedge } from '@/store/option/selectors/optionSelectors.ts';
import { optionSlice } from '@/store/option/slice.ts';
import type { HedgeType, LegId } from '@/store/option/state.ts';
import type { IntlMessageId } from '@/types/intl';

type SpotForwardRefProps = {
  tileId: string;
};

export function SpotRef({ tileId }: SpotForwardRefProps) {
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const [checkOption] = useCheckOption();
  const dispatch = useAppDispatch();
  const legId: LegId = `${tileId}/0`;
  const { hedgeSpotPrice, rfsHedgeSpotPrice, hedgeType, hedgeLegId } = useOptionSelector((option) => {
    const rfsHedge = selectQuoteHedge(option, legId);
    const hedge = selectOptionHedge(option, legId);

    const rfsHedgeSpotPrice = rfsHedge?.rateMid;

    return {
      hedgeSpotPrice: hedge?.rate,
      rfsHedgeSpotPrice,

      rfsId: getRfsId(option),
      hedgeLegId: hedge?.legId,
      hedgeType: option.properties.hedgeType,
    };
  });

  const isPinned = isDefined(hedgeSpotPrice);

  function handleChange(hedgeSpot: number | undefined | null) {
    sgwtWebAnalytics?.trackEvent('Option', 'SpotForward ref change', String(hedgeSpot));

    if (isNotDefined(hedgeLegId)) {
      return;
    }

    // This works around a bug in trade capture where, sometimes, the hedge amount is not returned by trade capture after being set
    // We set it to the right value optimistically, and then if trade capture does not return the value correctly, it will be set to undefined again
    dispatch(optionSlice.actions.optimisticUpdateHedgeRate({ tileId, hedgeLegId, hedgeRate: hedgeSpot ?? undefined }));
    return checkOption({
      tileId,
      hedges: { legId: hedgeLegId, rate: hedgeSpot },
    });
  }

  if (hedgeType === 'Live') {
    return null;
  }

  const fieldLabel = getFieldLabel(hedgeType);
  const displayedHedgeSpotValue = hedgeSpotPrice ?? rfsHedgeSpotPrice;

  return (
    <OptionFormGroup
      label={<FormattedMessage id={fieldLabel} />}
      addon={
        isPinned && (
          <button
            onClick={() => handleChange(null)}
            type="button"
            className="btn btn-sm btn-icon btn-flat-primary"
            data-e2e="spotRef-pin"
          >
            <em className="icon icon-sm cursor-default line-height-1 text-secondary">push_pin</em>
          </button>
        )
      }
    >
      <SgNumberInput
        valuePosition="start"
        size="sm"
        onChange={handleChange}
        className={isPinned ? 'text-info fw-bold ' : undefined}
        data-e2e="spotRef"
        acceleratorsEnabled
        value={displayedHedgeSpotValue}
      />
    </OptionFormGroup>
  );
}

function getFieldLabel(hedgeType: Exclude<HedgeType, 'Live'>): IntlMessageId {
  switch (hedgeType) {
    case 'Spot':
      return 'option.fields.spot.ref';
    case 'Forward':
      return 'option.fields.forward.ref';
    case 'Ndf':
      return 'option.fields.ndf.ref';
    default:
      return assertUnreachable(hedgeType, `Unhandled hedge type ${hedgeType}`);
  }
}
